import React, { useState, useRef, useEffect } from "react";
import { Mail, ArrowLeft, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { getAnonFavs, clearAnonFavs } from "../lib/storage";
import { LOGO_URL } from "../lib/brand";

export default function EmailAuth({ onSignedIn }) {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [stage, setStage] = useState("input"); // input | code
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const inputs = useRef([]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((x) => x - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/discover";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const requestCode = async () => {
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSending(true);
    try {
      await api.post("/auth/email/request-code", { email });
      toast.success("Code sent — check your inbox.");
      setStage("code");
      setResendIn(60);
      setTimeout(() => inputs.current[0]?.focus(), 50);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send code.");
    } finally {
      setSending(false);
    }
  };

  const onCellChange = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...code];
    next[i] = val;
    setCode(next);
    if (val && i < 5) inputs.current[i + 1]?.focus();
    if (val && i === 5 && next.every((d) => d !== "")) {
      verify(next.join(""));
    }
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !code[i] && i > 0) inputs.current[i - 1]?.focus();
  };

  const onPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      e.preventDefault();
      setCode(pasted.split(""));
      verify(pasted);
    }
  };

  const verify = async (codeStr) => {
    setVerifying(true);
    try {
      const r = await api.post("/auth/email/verify-code", { email, code: codeStr });
      signIn(r.data.session_token, r.data.user);
      // sync anon favs
      const anon = getAnonFavs();
      if (anon.length) {
        try { await api.post("/favorites/sync", { location_ids: anon }); } catch {}
        clearAnonFavs();
      }
      toast.success(`Welcome${r.data.user.name ? ", " + r.data.user.name.split(" ")[0] : ""}!`);
      onSignedIn && onSignedIn();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't verify code.");
      setCode(["", "", "", "", "", ""]);
      inputs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="flex flex-col items-center text-center flex-1 justify-center" data-testid="email-auth">
      {/* 1. Logo */}
      <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-brand-border shadow-sm bg-brand-sand">
        <img src={LOGO_URL} alt="Ozzy" className="w-full h-full object-cover" />
      </div>
      {/* 2. Headline + subtitle */}
      <h1 className="font-heading text-3xl font-semibold mt-5 text-brand-text">Welcome to Basecamp</h1>
      <p className="text-brand-subtle mt-2 max-w-sm">Sign in to sync favourites, save offline and post dog-first reviews.</p>

      {stage === "input" && (
        <div className="w-full mt-8 space-y-4 text-left">
          {/* 3. Google */}
          <button
            data-testid="google-signin-btn"
            onClick={startGoogle}
            className="w-full td-btn-primary"
          >
            Continue with Google
          </button>
          {/* 4. Divider */}
          <div className="flex items-center gap-3 text-xs text-brand-subtle">
            <div className="h-px bg-brand-border flex-1" />
            — or —
            <div className="h-px bg-brand-border flex-1" />
          </div>
          {/* 5. Email input */}
          <div>
            <label className="td-section-label" htmlFor="email-input">Email address</label>
            <input
              id="email-input"
              data-testid="email-input"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.co.uk"
              className="td-input mt-2"
            />
          </div>
          {/* 6. Send code */}
          <button
            data-testid="send-code-btn"
            disabled={sending}
            onClick={requestCode}
            className="w-full td-btn-secondary"
          >
            <Mail size={18} /> {sending ? "Sending…" : "Send login code"}
          </button>
        </div>
      )}

      {stage === "code" && (
        <div className="w-full mt-8 space-y-5">
          <button
            onClick={() => { setStage("input"); setCode(["","","","","",""]); }}
            data-testid="change-email-btn"
            className="text-sm text-brand-subtle hover:text-brand-text flex items-center gap-1.5"
          >
            <ArrowLeft size={14} /> Change email
          </button>
          <p className="text-sm text-brand-subtle">
            We sent a 6-digit code to <span className="text-brand-text font-medium">{email}</span>
          </p>
          <div className="flex justify-center gap-2" onPaste={onPaste}>
            {code.map((c, i) => (
              <input
                key={i}
                data-testid={`otp-cell-${i}`}
                ref={(el) => (inputs.current[i] = el)}
                inputMode="numeric"
                maxLength={1}
                value={c}
                onChange={(e) => onCellChange(i, e.target.value)}
                onKeyDown={(e) => onKeyDown(i, e)}
                className="w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-heading font-medium bg-brand-surface border border-brand-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
              />
            ))}
          </div>
          <button
            data-testid="verify-btn"
            onClick={() => verify(code.join(""))}
            disabled={verifying || code.some((c) => !c)}
            className="w-full td-btn-primary"
          >
            {verifying ? "Verifying…" : "Verify & sign in"}
          </button>
          <button
            data-testid="resend-btn"
            disabled={resendIn > 0 || sending}
            onClick={requestCode}
            className="text-sm text-brand-subtle hover:text-brand-text inline-flex items-center gap-1.5 mx-auto"
          >
            <RotateCw size={14} /> {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
          </button>
        </div>
      )}
    </div>
  );
}
